/**
 * Client-side helpers for the SPEI account identifier (CLABE).
 *
 * The API is the authority — it revalidates on save and the connector revalidates before a
 * transfer leaves — so everything here exists for immediate feedback while typing. The check-digit
 * arithmetic mirrors `@aventuria/shared`, which the web bundle deliberately does not import.
 */

/** Keeps only digits and caps at 18 — an autofilled e-mail or pasted text can never survive it. */
export function sanitizeClabe(value: string): string {
  return value.replace(/\D/g, "").slice(0, 18);
}

/** Banxico standard: first 17 digits weighted 3, 7, 1 cyclically; control digit closes mod 10. */
export function clabeCheckDigit(first17: string): number {
  const weights = [3, 7, 1];
  let sum = 0;
  for (let index = 0; index < 17; index += 1) {
    sum += (Number(first17[index]) * weights[index % 3]!) % 10;
  }
  return (10 - (sum % 10)) % 10;
}

export function isValidClabe(value: string): boolean {
  return /^\d{18}$/.test(value) && clabeCheckDigit(value.slice(0, 17)) === Number(value[17]);
}

export function maskClabe(value: string): string {
  return `••••${value.slice(-4)}`;
}

/** Standard 3-3-11-1 reading groups: bank, plaza, account, control digit. */
export function formatClabe(value: string): string {
  const digits = sanitizeClabe(value);
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6, 17), digits.slice(17, 18)]
    .filter(Boolean)
    .join(" ");
}

/**
 * ABM institution codes for the CLABE's first three digits. A partial, display-only list: an
 * unknown prefix is not an error (the authoritative catalogue is Kuspit's `catalogos/bancos`),
 * it just shows no bank name.
 */
const BANKS: Record<string, string> = {
  "002": "Citibanamex",
  "012": "BBVA México",
  "014": "Santander",
  "021": "HSBC",
  "030": "BanBajío",
  "036": "Inbursa",
  "044": "Scotiabank",
  "058": "Banregio",
  "072": "Banorte",
  "127": "Banco Azteca",
  "137": "BanCoppel",
  "646": "STP",
  "653": "Kuspit",
  "722": "Mercado Pago"
};

export function bankNameFromClabe(value: string): string | null {
  const digits = sanitizeClabe(value);
  return digits.length >= 3 ? BANKS[digits.slice(0, 3)] ?? null : null;
}
